import ListCreateButton from 'components/buttons/ListCreateButton';
import { LineTab, LineTabs, LineTabsList } from 'components/LineTabs';
import PageHeader from 'components/PageHeader';
import {
    CASE_VIEWS,
    ListViewsPageContext,
    SAVED_VIEW_PARAM_KEY,
    ViewSearchParamsContext,
} from 'components/tables/list-table/filter-bar/builtInViews';
import { Tooltip } from 'components/ui/tooltip';
import { useIsNewDesign } from 'components/tables/list-table/filter-bar/useIsNewDesign';
import PaddedLayout from 'layout/MainLayout/PaddedLayout';
import { FormattedMessage, useIntl } from 'react-intl';
import { useLocation, useNavigate } from 'react-router-dom';
import { useNavigateWithReturnState } from 'utils/useNavigateWithReturnState';
import MattersTableWithFilters from './MattersTableWithFilters';
import { NewCasesView } from './new-cases/NewCasesView';

type TabType = 'main' | 'new';

const NEW_CASES_PATH = '/matters/new-cases';

// ==============================|| MATTERS LIST PAGE ||============================== //

const MattersListPage = (): JSX.Element => {
    const intl = useIntl();
    const navigateWithReturn = useNavigateWithReturnState();
    const location = useLocation();
    const navigate = useNavigate();
    const isNew = useIsNewDesign();

    // MAIN keeps the bare /matters, so the filters its users remember under that path still apply.
    const currentTab: TabType = location.pathname.replace(/\/+$/, '') === NEW_CASES_PATH ? 'new' : 'main';
    const savedView = new URLSearchParams(location.search).get(SAVED_VIEW_PARAM_KEY);
    const activeView = CASE_VIEWS.find((view) => view.id === savedView) ?? CASE_VIEWS[0];

    return (
        <PaddedLayout className="overflow-hidden">
            <div className="flex h-full flex-col gap-4">
                <PageHeader
                    hostsList
                    // New cases is a report of its own in the new design, reached from the menu rather than a tab.
                    title={intl.formatMessage({
                        id: isNew && currentTab === 'new' ? 'matters.tabs.new-tooltip' : 'matters.matters',
                    })}
                    showBreadcrumb
                    buttons={
                        <ListCreateButton
                            permissions={['CREATE.MATTERS', 'CREATE.MATTERS.CLIENTS']}
                            variant="contained"
                            color="secondary"
                            size="small"
                            onClick={() => navigateWithReturn(`/matter/new`)}
                            labelId="matters.add-new-matter"
                            newLabelId="matters.new-case"
                        />
                    }
                />
                {isNew ? null : (
                    <LineTabs
                        value={currentTab}
                        onValueChange={(value) => navigate(value === 'new' ? NEW_CASES_PATH : '/matters')}
                    >
                        <LineTabsList>
                            <Tooltip content={<FormattedMessage id="matters.tabs.main-tooltip" />}>
                                <LineTab value="main">
                                    <FormattedMessage id="matters.tabs.main" />
                                </LineTab>
                            </Tooltip>
                            <Tooltip content={<FormattedMessage id="matters.tabs.new-tooltip" />}>
                                <LineTab value="new">
                                    <FormattedMessage id="matters.tabs.new" />
                                </LineTab>
                            </Tooltip>
                        </LineTabsList>
                    </LineTabs>
                )}
                {isNew && currentTab === 'main' ? (
                    // The list renders the views row. Keyed by built-in view: each opens on its own filters.
                    <ListViewsPageContext.Provider
                        value={{ listKey: 'matters', views: CASE_VIEWS, activeId: activeView.id }}
                    >
                        <ViewSearchParamsContext.Provider value={activeView.searchParams}>
                            <MattersTableWithFilters key={activeView.id} />
                        </ViewSearchParamsContext.Provider>
                    </ListViewsPageContext.Provider>
                ) : currentTab === 'main' ? (
                    <MattersTableWithFilters />
                ) : (
                    <NewCasesView />
                )}
            </div>
        </PaddedLayout>
    );
};

export default MattersListPage;
